import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { getDefaultRedirect } from '@/features/auth/context/AuthContext';
import { PhoneVerification } from '@/features/phone-verification/components/PhoneVerification';
import {
  needsPhoneVerification,
  PHONE_VERIFICATION_PATH,
} from '@/features/phone-verification/utils/phoneGate';

/** Accepts only in-app paths, so a crafted state cannot bounce the user off-site. */
function safeRedirect(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value === PHONE_VERIFICATION_PATH ? null : value;
}

/**
 * Blocking OTP step for accounts that never verified their phone number —
 * typically someone who skipped the code during registration and comes back
 * later. The code is generated on demand here (10 minutes of validity), so an
 * abandoned registration needs nothing carried over.
 *
 * SMS is only sent when the user presses "Invia codice" (no autoStart): landing
 * on this page must not burn the hourly OTP quota.
 */
export function VerifyPhonePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, refreshUser, logout } = useAuth();
  // Bumping the key remounts PhoneVerification back to its phone stage, which
  // is the only way out for someone who typed the wrong number.
  const [attempt, setAttempt] = useState(0);

  // Dove tornare dopo la verifica: il redirect che l'utente stava inseguendo
  // prima del gate, altrimenti la home del suo ruolo.
  const target =
    safeRedirect((location.state as { redirectTo?: unknown } | null)?.redirectTo) ??
    (user ? getDefaultRedirect(user.role) : '/');

  if (isLoading) return null;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  if (!needsPhoneVerification(user)) return <Navigate to={target} replace />;

  const handleVerified = async () => {
    await refreshUser();
    navigate(target, { replace: true });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4 py-8"
      style={{
        background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
        // Spazio per il banner cookie: senza, su telefono "Invia codice" finisce
        // sotto al banner e il tap non arriva mai al bottone.
        paddingBottom: 'calc(2rem + var(--cookie-banner-h, 0px))',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(24,153,170,0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(118,185,185,0.08) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-white">
              Verifica il tuo numero
            </h1>
            <p className="mt-1.5 text-sm text-white/60">
              Per continuare devi confermare il tuo numero di telefono. Ti invieremo un codice
              via SMS.
            </p>
          </div>

          <PhoneVerification
            key={attempt}
            tone="dark"
            initialPhone={user.profile?.pendingPhone ?? user.profile?.phone ?? ''}
            onVerified={handleVerified}
          />

          <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-sm">
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="text-white/40 transition-colors hover:text-white/70"
            >
              Numero sbagliato?
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="font-medium text-brand-teal transition-colors hover:text-brand-cyan"
            >
              Esci
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
